import { useState, useEffect, useRef } from 'react';
import {
  PlusCircle,
  DatabaseZap,
  ChevronDown,
  Sparkles,
  Tag,
  X,
  Plus,
  AlertTriangle,
  Zap,
  Brain,
  BookOpen,
  HelpCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ChatSuggestionChips from '../ChatChips';
import EditableBetSlipItem from '../virtualBetSlip/EditableBetSlipItem';
import ParlayExporter from '../virtualBetSlip/ParlayExporter';
import MobileTopBanner from './mobile/MobileTopBanner';
import { parseBetWithAI, enhanceBetWithOdds } from '../../services/betParser';
import { StickyBannerWrapper } from '../chat/MobileTopBannerSticky';
import DesktopTeamStats from './TopDesktopTeamStats';
import BetaBanner from './BetaBanner';
import TeamStatsMenu from './TeamDesktopStatsMenu';
import GamePredictionButton from '../gamePrediction/GamePredictionButton';
import CustomPromptsTab from '../customPrompts/CustomPromptsTab';
import StarCoinsDisplay from '../starCoinSystem/StarCoinsDisplay';

// Import language system and mappings
import { createTranslator } from '../../utils/detectLanguage';
import { topChatBannerPtBR } from '../mappings/topChatBanner.pt-br';
import { topChatBannerES } from '../mappings/topChatBanner.es';


const TopChatBanner = ({
  userStats,
  handleNewChat,
  onSuggestionClick,
  userId,
  onShowMobileStats,
  isMobile,
  updateUIState,
  RESPONSE_MODES,
  responseMode,
  tier,
  gameUsageState,
  runAutomatedGamePrediction,
  currentGameInfo = null, // Current game context if available
  onHelpClick, // Prop for triggering onboarding tour
}) => {
  // Translation helper
  const t = createTranslator({
    'pt-br': topChatBannerPtBR,
    'es': topChatBannerES
  });

  // State hooks
  const [showSuggestionsDropdown, setShowSuggestionsDropdown] = useState(false);
  const [showResponseModes, setShowResponseModes] = useState(false);
  const [showBetaBanner, setShowBetaBanner] = useState(false);
  const [activeSuggestionTab, setActiveSuggestionTab] = useState('prompts');

  // BetSlip state
  const [bets, setBets] = useState([]);
  const [showBetSlip, setShowBetSlip] = useState(false);
  const [showConfirmClear, setShowConfirmClear] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPickText, setNewPickText] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Refs
  const suggestionsButtonRef = useRef(null);
  const suggestionsDropdownRef = useRef(null);
  const responseModeRef = useRef(null);
  const betSlipRef = useRef(null);
  const betaButtonRef = useRef(null);
  
  // Close dropdowns when clicking outside - only applied to desktop
  useEffect(() => {
    if (isMobile) return;
    
    const handleClickOutside = (event) => {
      // For response modes dropdown
      if (
        responseModeRef.current && 
        !responseModeRef.current.contains(event.target) && 
        showResponseModes
      ) {
        setShowResponseModes(false);
      }
      
      // For desktop - only handle click outside for betslip
      if (
        betSlipRef.current && 
        !betSlipRef.current.contains(event.target) && 
        !event.target.closest('[data-betslip-trigger]')
      ) {
        setShowBetSlip(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showResponseModes, isMobile]);
  
  // Load bets from localStorage on mount
  useEffect(() => {
    if (userId) {
      try {
        const savedBets = localStorage.getItem(`betslip_${userId}`);
        if (savedBets) {
          setBets(JSON.parse(savedBets));
        }
      } catch (err) {
        console.error("Error loading bets from localStorage:", err);
      }
    }
  }, [userId]);
  
  // Save bets to localStorage whenever they change
  useEffect(() => {
    if (userId) {
      localStorage.setItem(`betslip_${userId}`, JSON.stringify(bets));
    }
  }, [bets, userId]);
  
  // Set up global method for adding bets with AI parsing
  useEffect(() => {
    // Define the function to add a bet with parsing
    const addBet = async (text) => {
      // Show loading state immediately
      const tempBet = {
        id: Date.now().toString(),
        text,
        timestamp: new Date().toLocaleString(),
        isParsing: true
      };
      
      setBets(prev => [...prev, tempBet]);
      setShowBetSlip(true);
      
      try {
        console.log('TopChatBanner - Starting bet parse for:', text);
        
        // Parse the bet with AI to get structured data
        const parsedData = await parseBetWithAI(text);
        console.log('TopChatBanner - Parsed data:', parsedData);
        
        // Enhance with odds data to get bookmaker IDs
        const enhancedData = await enhanceBetWithOdds(parsedData);
        console.log('TopChatBanner - Enhanced data:', enhancedData);
        
        // Update the bet with parsed data
        const updatedBet = { ...tempBet, isParsing: false, parsedData: enhancedData };
        console.log('TopChatBanner - Storing bet:', updatedBet);
        
        setBets(prev => prev.map(bet => 
          bet.id === tempBet.id ? updatedBet : bet
        ));
      } catch (error) {
        console.error('TopChatBanner - Error parsing bet:', error);
        // Remove parsing flag on error
        setBets(prev => prev.map(bet => 
          bet.id === tempBet.id 
            ? { ...bet, isParsing: false }
            : bet
        ));
      }
    };
    
    // Expose the function globally
    window.addToBetSlip = addBet;
    
    return () => {
      // Clean up when component unmounts
      delete window.addToBetSlip;
    };
  }, []);

  // Handle suggestion selection
  const handleSuggestionSelect = (prompt) => {
    setShowSuggestionsDropdown(false);
    if (onSuggestionClick) {
      onSuggestionClick(prompt);
    }
  };
  
  // BetSlip handlers
  const removeBet = (id) => {
    setDeleteTargetId(id);
    setShowDeleteConfirm(true);
  };
  
  const handleConfirmDelete = () => {
    if (deleteTargetId) {
      setBets(prev => prev.filter(bet => bet.id !== deleteTargetId));
      setDeleteTargetId(null);
    }
    setShowDeleteConfirm(false);
  };
  
  const updateBet = (id, newText) => {
    setBets(prev => 
      prev.map(bet => 
        bet.id === id ? { ...bet, text: newText } : bet
      )
    );
  };
  
  const handleClearAll = () => {
    setShowConfirmClear(true);
  };
  
  const handleConfirmClear = () => {
    setBets([]);
    setShowConfirmClear(false);
  };
  
  const handleManualAdd = () => {
    if (newPickText.trim()) {
      window.addToBetSlip(newPickText);
      setNewPickText('');
      setShowAddModal(false);
    }
  };

  const handleExportToClipboard = () => {
    // Format the bets into a neat text format
    const exportText = bets.map(bet => `• ${bet.text}`).join('\n');
    const fullText = `My Picks:\n${exportText}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(fullText)
      .then(() => {
        // Show success notification
        alert('Picks copied to clipboard!');
        // In a real app, use a toast notification instead of alert
      })
      .catch(err => {
        console.error('Failed to copy picks:', err);
        alert('Failed to copy picks to clipboard. Please try again.');
      });
  };


// Handle prompt selection - cutsom prmpts
const handlePromptUse = (promptText) => {
  // Close suggestions dropdown
  setShowSuggestionsDropdown(false);
  
  // Use prompt in chat
  if (onSuggestionClick) {
    onSuggestionClick(promptText);
  }
};

// Handle prompt limit reached
const handlePromptLimitReached = () => {
  updateUIState({ showUpgradePrompt: true });
};


  // Add an effect to log when isMobile changes
  useEffect(() => {
    console.log('TopChatBanner: isMobile changed to', isMobile);
  }, [isMobile]);
  
  // Conditional rendering for mobile
  if (isMobile) {
    return (
      <StickyBannerWrapper className="mobile-top-banner-wrapper" data-top-banner="true"> {/* data-top-banner for mobile onboarding */}
        <MobileTopBanner
        userId={userId}
        userStats={userStats}
        responseMode={responseMode}
        updateUIState={updateUIState}
        RESPONSE_MODES={RESPONSE_MODES}
        handleNewChat={handleNewChat}
        onSuggestionClick={onSuggestionClick}
        onShowMobileStats={onShowMobileStats}
        bets={bets}
        setBets={setBets}
        removeBet={removeBet}
        updateBet={updateBet}
        handleClearAll={handleClearAll}
        handleManualAdd={handleManualAdd}
        handleExportToClipboard={handleExportToClipboard}
        showConfirmClear={showConfirmClear}
        setShowConfirmClear={setShowConfirmClear}
        showAddModal={showAddModal}
        setShowAddModal={setShowAddModal}
        newPickText={newPickText}
        setNewPickText={setNewPickText}
        deleteTargetId={deleteTargetId}
        setDeleteTargetId={setDeleteTargetId}
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        handleConfirmDelete={handleConfirmDelete}
        tier={tier}
        gameUsageState={gameUsageState}
        runAutomatedGamePrediction={runAutomatedGamePrediction}
        currentGameInfo={currentGameInfo}
        />
      </StickyBannerWrapper>
    );
  }

  // Get current selected mode icon from RESPONSE_MODES
  const SelectedModeIcon = RESPONSE_MODES && responseMode ? RESPONSE_MODES[responseMode]?.icon : null;

  // Desktop version JSX
  return (
    <div data-top-banner="true" className="sticky top-0 z-20 w-full bg-gradient-to-b from-slate-900 to-slate-800/95 backdrop-blur-md border-b border-white/5 shadow-lg"> {/* data-top-banner for onboarding targeting */}
      {/* Main banner content */}
      <div className="w-full max-w-7xl mx-auto px-4 py-2.5 relative">
        <div className="flex items-center">
          {/* Logo */}
          <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-violet-300 flex items-center gap-2 mr-6">
            <Zap className="h-5 w-5 text-emerald-300" />
            <span>PickStar AI</span>
          </h1>
          
          {/* All buttons in a single flex container with no ml-auto */}
          <div className="flex items-center">


            {/* AI Game Prediction Button */}
            <div className="relative mr-3">
              <GamePredictionButton
                onClick={() => {
                  if (gameUsageState.canUse) {
                    runAutomatedGamePrediction();
                  } else {
                    if (tier === 'basic') {
                      updateUIState({ showUpgradePrompt: true });
                    } else {
                      // For pro users, directly open the prediction modal to show reset info
                      runAutomatedGamePrediction();
                    }
                  }
                }}
                remainingSpins={gameUsageState.remaining}
                isBasicUser={tier === 'basic'}
                isMobile={false}
                resetTime={gameUsageState.resetTime}
              />
            </div>

            {/* Game Explorer - Team Stats Menu Component */}
            <div className="mr-3">
              <TeamStatsMenu 
                onSuggestionClick={onSuggestionClick}
                DesktopTeamStats={DesktopTeamStats}
                isMobile={isMobile}
              />
            </div>


            {/* AI Tool Kit Button - Now with My Prompts */}
            <div className="relative mr-3">
              <button
                onClick={() => setShowSuggestionsDropdown(!showSuggestionsDropdown)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all text-emerald-400 hover:text-emerald-300 border border-emerald-500/20 hover:border-emerald-500/30 bg-emerald-500/10 shadow-sm"
                aria-label="AI Suggestions & Prompts"
              >
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">{t('AI Toolkit')}</span>
              </button>
            </div>


            {/* Pick Slip Button */}
            <div className="relative mr-3">
              <button
                onClick={() => setShowBetSlip(!showBetSlip)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all text-emerald-400 hover:text-emerald-300 border border-emerald-500/20 hover:border-emerald-500/30 bg-emerald-500/10 shadow-sm"
                title="View bet slip"
                data-betslip-trigger
              >
                <Tag className="w-4 h-4" />
                <span className="text-sm font-medium">{t('Pick Slip')}</span>
                {/* Number indicator */}
                {bets.length > 0 && (
                  <span className="flex items-center justify-center h-5 w-5 bg-emerald-500 text-white text-xs font-semibold rounded-full">
                    {bets.length}
                  </span>
                )}
              </button>
            </div>

          {/* Add Star Coins Display */}
          <div className="mr-3">
          <StarCoinsDisplay userId={userId} />
              </div>

            {/* Response Mode Dropdown */}
            {RESPONSE_MODES && responseMode && (
              <div className="relative mr-3" ref={responseModeRef}>
                <button
                  data-chat-response-modes="true" // Data attribute for onboarding (if needed)
                  onClick={() => setShowResponseModes(!showResponseModes)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all text-emerald-400 hover:text-emerald-300 border border-emerald-500/20 hover:border-emerald-500/30 bg-emerald-500/10 shadow-sm"
                  aria-label="Response mode"
                >
                  {SelectedModeIcon && <SelectedModeIcon className="w-4 h-4" />}
                  <span className="text-sm font-medium">
                    {responseMode === 'normal' ? t('Standard') : 
                     responseMode === 'quick' ? t('Quick') : t('Detailed')}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${showResponseModes ? 'rotate-180' : ''}`} />
                </button>

                {/* Response Mode Dropdown */}
                <AnimatePresence>
                  {showResponseModes && (
                    <motion.div
                      initial={{ opacity: 0, y: -5, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -5, scale: 0.98 }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                      className="absolute left-0 top-full mt-1.5 bg-slate-800 border border-slate-700/80 rounded-lg shadow-xl z-50 w-56 overflow-hidden"
                    >
                      <div className="py-1">
                        {Object.entries(RESPONSE_MODES).map(([mode, { icon: Icon }]) => {
                          const isActive = mode === responseMode;
                          
                          // Customized labels and descriptions based on the mode
                          const label = mode === 'normal' ? 'Balanced Response' : 
                                      mode === 'quick' ? 'Quick & Concise' : 'Expert Analysis';

                          const description = mode === 'normal' ? 'Standard detail and context' : 
                                            mode === 'quick' ? 'Short, direct answers' : 
                                            'Comprehensive & detailed insights';
                          
                          return (
                            <button
                              key={mode}
                              onClick={() => {
                                updateUIState({ responseMode: mode });
                                setShowResponseModes(false);
                              }}
                              className={`w-full text-left p-2.5 flex items-start gap-2.5 transition-colors ${
                                isActive 
                                  ? 'bg-emerald-500/15 text-emerald-300' 
                                  : 'hover:bg-slate-700/70 text-white/80'
                              }`}
                            >
                              <Icon className={`w-5 h-5 mt-0.5 ${isActive ? 'text-emerald-400' : 'text-white/70'}`} />
                              <div>
                                <div className="text-sm font-medium">{label}</div>
                                <div className="text-xs text-white/60 mt-0.5">{description}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Beta Button */}
            <div className="relative mr-3" ref={betaButtonRef}>
              <button
                onClick={() => setShowBetaBanner(true)}
                className="flex items-center gap-2 px-3.5 py-1.5 rounded-full transition-all bg-gradient-to-r from-emerald-500/20 to-violet-500/20 text-emerald-300 hover:text-emerald-200 hover:from-emerald-500/30 hover:to-violet-500/30 border border-emerald-500/20 shadow-sm"
                aria-label="Beta Info"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                <span className="text-sm font-medium">{t('v4')}</span>
              </button>
            </div>

          
            {/* Divider */}
            <div className="h-6 w-px bg-slate-700/50 mr-3"></div>
            
            {/* Chat History Button */}
            <button
              data-chat-history-button="true" // Data attribute for onboarding targeting
              onClick={onShowMobileStats}
              className="flex items-center justify-center h-8 w-8 rounded-lg bg-slate-800 text-emerald-300 hover:text-emerald-300 hover:bg-slate-700 transition-colors shadow-sm mr-2"
              title="View chat history"
            >
              <DatabaseZap className="w-4 h-4" />
            </button>

            {/* New Chat Button */}
            <button
              onClick={handleNewChat}
              className="flex items-center justify-center h-8 w-8 rounded-lg bg-slate-800 text-emerald-300 hover:text-emerald-300 hover:bg-slate-700 transition-colors shadow-sm mr-2"
              title="Start new chat"
            >
              <PlusCircle className="w-4 h-4" />
            </button>

            {/* Help Button - Triggers onboarding tour */}
            {onHelpClick && (
              <button
                onClick={onHelpClick} // Triggers chat onboarding when clicked
                className="flex items-center justify-center h-8 w-8 rounded-lg bg-slate-800 text-emerald-300 hover:text-emerald-300 hover:bg-slate-700 transition-colors shadow-sm"
                title="Show chat tour"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Desktop Suggestions Panel with uniform grid layout */}
{/* Enhanced Suggestions Panel with Custom Prompts Tab */}
<AnimatePresence>
        {showSuggestionsDropdown && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="w-full bg-gradient-to-b from-slate-800/95 to-slate-900/95 backdrop-blur-xl border-b border-white/5 shadow-xl overflow-hidden"
          >
            <div className="w-full max-w-6xl mx-auto px-4 py-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center h-8 w-8 bg-gradient-to-br from-emerald-500 to-violet-600 rounded-lg shadow-md">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-medium text-white">PickStar AI Suggestions</h3>
                </div>
                <button 
                  onClick={() => setShowSuggestionsDropdown(false)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full text-white/70 hover:text-white bg-white/5 hover:bg-white/10 transition-colors overflow-y-auto"
                >
                  <span className="text-sm">Close</span>
                </button>
              </div>
              
              <div className="h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent mb-5"></div>
              
              {/* Tab navigation - Now with My Prompts tab */}
              <div className="mb-5">
                <div className="flex space-x-8">
                
              {/* My Prompts Tab */}
              <button 
                    onClick={() => setActiveSuggestionTab('prompts')} 
                    className={`relative pb-2 text-sm font-medium transition-colors flex items-center group ${
                      activeSuggestionTab === 'prompts' 
                        ? 'text-emerald-400' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <BookOpen className="w-4 h-4 mr-1.5" />
                    <span>My Prompts</span>
                    {activeSuggestionTab === 'prompts' && (
                      <motion.div 
                        layoutId="tabIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400 to-violet-400"
                      />
                    )}
                  </button>


                  <button 
                    onClick={() => setActiveSuggestionTab('default')} 
                    className={`relative pb-2 text-sm font-medium transition-colors group ${
                      activeSuggestionTab === 'default' 
                        ? 'text-emerald-400' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <span>Popular Picks</span>
                    {activeSuggestionTab === 'default' && (
                      <motion.div 
                        layoutId="tabIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400 to-violet-400"
                      />
                    )}
                  </button>
                  
                  
                  <button 
                    onClick={() => setActiveSuggestionTab('soccer')} 
                    className={`relative pb-2 text-sm font-medium transition-colors flex items-center group ${
                      activeSuggestionTab === 'soccer' 
                        ? 'text-emerald-400' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <span>Soccer</span>
                    {activeSuggestionTab === 'soccer' && (
                      <motion.div 
                        layoutId="tabIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400 to-violet-400"
                      />
                    )}
                  </button>
                  
                  <button 
                    onClick={() => setActiveSuggestionTab('advanced')} 
                    className={`relative pb-2 text-sm font-medium transition-colors group ${
                      activeSuggestionTab === 'advanced' 
                        ? 'text-emerald-400' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <span>Advanced Analysis</span>
                    {activeSuggestionTab === 'advanced' && (
                      <motion.div 
                        layoutId="tabIndicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400 to-violet-400"
                      />
                    )}
                  </button>
                </div>
              </div>
              
{/* Content area */}
<div className="bg-slate-800/70 rounded-xl border border-white/5 shadow-inner">
                <div className="p-16">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeSuggestionTab}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {activeSuggestionTab === 'default' && (
                        <ChatSuggestionChips 
                          onSuggestionClick={handleSuggestionSelect} 
                          variant="default"
                          displayMode="uniform"
                        />
                      )}
                      

                      {activeSuggestionTab === 'prompts' && (
                        <CustomPromptsTab 
                          userId={userId}
                          onUsePrompt={handlePromptUse}
                          gameInfo={currentGameInfo}
                          onPromptLimitReached={handlePromptLimitReached}
                          onUpgrade={() => updateUIState({ showUpgradePrompt: true })}
                        />
                      )}
                      
                      {activeSuggestionTab === 'soccer' && (
                        <ChatSuggestionChips 
                          onSuggestionClick={handleSuggestionSelect} 
                          variant="soccer"
                          displayMode="uniform"
                        />
                      )}
                      
                      {activeSuggestionTab === 'advanced' && (
                        <ChatSuggestionChips 
                          onSuggestionClick={handleSuggestionSelect} 
                          variant="advanced"
                          displayMode="uniform"
                        />
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop BetSlip Dropdown - Full width extension like AI Toolkit */}
      <AnimatePresence>
        {!isMobile && showBetSlip && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="w-full bg-gradient-to-b from-slate-800/95 to-slate-900/95 backdrop-blur-xl border-b border-white/5 shadow-xl overflow-hidden"
          >
            <div className="w-full max-w-6xl mx-auto px-4 py-5">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-white">My Picks</h3>
                  {bets.length > 0 && (
                    <span className="bg-white/10 text-white/90 text-xs px-2 py-0.5 rounded-full font-medium">
                      {bets.length}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowBetSlip(false)}
                  className="text-xs text-white/60 hover:text-white/90 transition-colors"
                >
                  Close
                </button>
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-3"></div>

              {/* Content Area */}
              <div className="bg-slate-800/70 rounded-lg border border-white/5 shadow-inner">
                <div className="p-3 max-h-[60vh] overflow-y-auto space-y-2">
                  {bets.length > 0 ? (
                    <>
                      {bets.map((bet, index) => (
                        <div key={bet.id}>
                          <EditableBetSlipItem
                            bet={bet}
                            onRemove={removeBet}
                            onUpdate={updateBet}
                            allBets={bets}
                            index={index}
                          />
                          {index < bets.length - 1 && (
                            <div className="h-px bg-white/5 my-2" />
                          )}
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="text-center py-8 px-4">
                      <p className="text-white/50 text-sm mb-1">No picks yet</p>
                      <p className="text-xs text-white/30">Add bets from AI suggestions</p>
                    </div>
                  )}
                </div>

                {/* Footer with actions */}
                {bets.length > 0 && (
                  <div className="p-2 border-t border-white/5 bg-slate-900/30">
                    <div className="flex items-center justify-between">
                      {/* Left side - utility buttons */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleClearAll}
                          className="px-2 py-1 text-xs text-white/40 hover:text-red-400/90 hover:bg-white/5 rounded transition-colors"
                          title="Clear all picks"
                        >
                          Clear All
                        </button>

                        <button
                          onClick={() => setShowAddModal(true)}
                          className="px-2 py-1 text-xs text-white/40 hover:text-white/70 hover:bg-white/5 rounded transition-colors"
                          title="Add manual pick"
                        >
                          Add Pick
                        </button>
                      </div>

                      {/* Right side - export options */}
                      <div className="flex items-center gap-2">
                        <ParlayExporter bets={bets} />
                        <button
                          onClick={handleExportToClipboard}
                          className="px-2 py-1 text-xs text-white/40 hover:text-white/70 hover:bg-white/5 rounded transition-colors"
                          title="Copy all picks to clipboard"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Add pick button when no bets */}
                {bets.length === 0 && (
                  <div className="p-2 border-t border-white/5">
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="w-full py-2 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white/90 rounded transition-colors text-sm"
                    >
                      Add Manual Pick
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Beta Banner Modal - Repositioned to drop down below beta button */}
      {/* Beta Banner Modal - Repositioned to drop down below beta button */}
      <AnimatePresence>
        {showBetaBanner && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-start justify-center pt-20 px-4"
            onClick={() => setShowBetaBanner(false)}
          >
            <motion.div 
              initial={{ y: -20, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -20, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 400 }}
              className="bg-slate-800 border border-slate-700/50 rounded-xl p-5 w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center h-8 w-8 bg-gradient-to-br from-emerald-500/20 to-violet-500/20 rounded-lg">
                    <Brain className="w-4 h-4 text-emerald-400" />
                  </div>
                  <h3 className="text-base font-medium text-white">PickStar v4.0</h3>
                </div>
                <button 
                  onClick={() => setShowBetaBanner(false)}
                  className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
                <div>
                  <h4 className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-violet-400 mb-2">Ultimate Agentic AI for Sports & Fantasy</h4>
                  <p className="text-white/80 text-sm leading-relaxed">
                    Unleash the power of v4 Agentic AI with revolutionary QuickPicks, StarCoin trading pools, and market analysis. Dominate sports betting and fantasy with our most intelligent system yet.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 border border-white/5 shadow-inner">
                  <h5 className="text-sm font-medium text-emerald-400 mb-3">🚀 New in v4.0!</h5>
                  <ul className="space-y-2.5 text-sm text-white/80">
                    <li className="flex items-start gap-2.5">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-xs">🤖</span>
                      <span>Hyper-Agentic AI: Next-level autonomous sports & fantasy analysis</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-xs">⚡</span>
                      <span>AI QuickPicks: Instant intelligent picks with market-beating insights</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-xs">💰</span>
                      <span>StarCoin Trading Pools: Every QuickPick opens a tradeable market</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-xs">📈</span>
                      <span>Advanced Market Analysis: Real-time line movements & value detection</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-xs">🏈</span>
                      <span>Enhanced NFL Tools: v3 agent-powered deep game breakdowns</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-xs">🎓</span>
                      <span>NCAA Football Arsenal: College game analysis with v3 intelligence</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-xs">🎯</span>
                      <span>Deeper Analysis Engine: Multi-layer insights beyond surface stats</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-xs">🥊</span>
                      <span>MMA Fight Analysis: Fighter stats, matchup insights & predictions</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-xs">🏐</span>
                      <span>Volleyball Coverage: Complete match analysis & betting strategies</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 text-xs">✨</span>
                      <span>Revolutionary Interface: Intuitive, beautiful, blazing fast</span>
                    </li>
                  </ul>
                  
                  <h5 className="text-sm font-medium text-pink-400 mt-5 mb-3">🔮 Coming Soon:</h5>
                  <ul className="space-y-2.5 text-sm text-white/80">
                    <li className="flex items-start gap-2.5">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-pink-500/20 text-pink-400 text-xs">🎾</span>
                      <span>Tennis betting with match analysis and player form</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-pink-500/20 text-pink-400 text-xs">⛳</span>
                      <span>Golf tournament predictions and player performance models</span>
                    </li>
                  </ul>
                </div>
                
                <div className="flex justify-center">
                  <a href="mailto:engineering@pickstarai.com" className="text-emerald-400 hover:text-emerald-300 text-sm">
                    engineering@pickstarai.com
                  </a>
                </div>
              </div>
              
              <div className="pt-5 mt-4 border-t border-slate-700/50">
                <button
                  onClick={() => setShowBetaBanner(false)}
                  className="w-full py-2.5 bg-gradient-to-r from-emerald-600 to-violet-600 hover:from-emerald-500 hover:to-violet-500 text-white rounded-lg font-medium shadow-sm transition-all"
                >
                  Let's Go!
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Other modals */}
      <AnimatePresence>
        {/* Clear All Confirmation Dialog */}
        {showConfirmClear && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-start justify-center pt-20 px-4"
            onClick={() => setShowConfirmClear(false)}
          >
            <motion.div 
              initial={{ y: -20, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -20, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 400 }}
              className="bg-slate-800 border border-slate-700/50 rounded-xl p-5 w-full max-w-xs shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-white text-lg font-medium mb-2">Clear all picks?</h4>
              <p className="text-slate-300 text-sm mb-4">
                Are you sure you want to remove all picks from your list?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmClear(false)}
                  className="flex-1 py-2 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded-lg text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmClear}
                  className="flex-1 py-2 bg-red-500/90 hover:bg-red-500 text-white rounded-lg text-sm transition-colors shadow-sm"
                >
                  Clear All
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* Delete Single Pick Confirmation Dialog */}
        {showDeleteConfirm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-start justify-center pt-20 px-4"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div 
              initial={{ y: -20, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -20, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 400 }}
              className="bg-slate-800 border border-slate-700/50 rounded-xl p-5 w-full max-w-xs shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-white text-lg font-medium mb-2">Delete this pick?</h4>
              <p className="text-slate-300 text-sm mb-4">
                Are you sure you want to remove this pick from your list?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded-lg text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="flex-1 py-2 bg-red-500/90 hover:bg-red-500 text-white rounded-lg text-sm transition-colors shadow-sm"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* Add Pick Modal */}
        {showAddModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-start justify-center pt-20 px-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div 
              initial={{ y: -20, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -20, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 400 }}
              className="bg-slate-800 border border-slate-700/50 rounded-xl p-5 w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center mb-4">
                <div className="flex items-center justify-center h-8 w-8 bg-emerald-500/20 rounded-lg mr-3">
                  <Plus className="w-4 h-4 text-emerald-400" />
                </div>
                <h4 className="text-white text-lg font-medium">Add Custom Pick</h4>
              </div>
              <textarea
                value={newPickText}
                onChange={(e) => setNewPickText(e.target.value)}
                placeholder="Enter your pick details..."
                className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg p-3 text-sm text-slate-200 resize-none h-32 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 mb-4 shadow-inner"
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2.5 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded-lg text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleManualAdd}
                  className="flex-1 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white rounded-lg text-sm transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!newPickText.trim()}
                >
                  Add Pick
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inline styles for dropdown */}
      <style>{`
        .suggestions-container::-webkit-scrollbar {
          width: 4px;
        }
        
        .suggestions-container::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 4px;
        }
        
        .suggestions-container::-webkit-scrollbar-thumb {
          background: rgba(52, 211, 153, 0.3);
          border-radius: 4px;
        }
        
        .suggestions-container::-webkit-scrollbar-thumb:hover {
          background: rgba(52, 211, 153, 0.5);
        }
      `}</style>
    </div>
  );
};

export default TopChatBanner;